import certificatesCollection from 'collections/certificates';
import { packagingService } from 'collections/services';
import { XOMETRY_VAT_ENABLED } from 'config/regions/features';
import { getRegionCurrency } from 'config/regions/utils';
import { each, find, groupBy, last, map } from 'es-toolkit/compat';
import { Certificates, Position, PositionsByKey, PositionsByShipping, ServerPosition, ServiceType } from 'interfaces';
import { OIShippingDetails } from 'interfaces/graphql';
import { action, computed, observable } from 'mobx';
import { RootStore } from 'stores/RootStore';
import { camelizeKeys } from 'utils/axios';
import { XMoney } from 'utils/money';
import { uniqID } from 'utils/uniqID';

class DealOutInvoicePositionsStore {
  rootStore: RootStore;

  currentPosition = 0;

  @observable tableLoading = false;

  @observable tablePositions: PositionsByKey = {};

  @observable positionsByShipping: PositionsByShipping = {};

  @observable sourceShipping?: OIShippingDetails;

  @observable vatRate?: number;

  parsePositions = (positions: ServerPosition[]): Position[] => {
    const newPositions: Position[] = positions.map((position) => ({
      categoryName: position.accounting_category,
      quantity: position.quantity || 1,
      price: position.price_per_piece,
      priceMoney: camelizeKeys(position.price_per_piece_money),
      providerPrice: position.provider_price,
      name: position.name,
      vat: XOMETRY_VAT_ENABLED ? (this.vatRate ?? position.vat_rate) : position.vat_rate,
      position: position.position,
      partId: position.part_id,
      refId: position.ref_id,
      refType: position.ref_type,
      refSource: position.ref_source,
      deliveryDate: position.delivery_date,
      id: position.id,
      key: uniqID(),
    }));

    this.currentPosition = last(newPositions)?.position || this.currentPosition;

    return newPositions;
  };

  incCurrentPosition = (inc = 10): number => {
    this.currentPosition += inc;

    return this.currentPosition;
  };

  @action setVatRate = (vatRate: number) => {
    this.vatRate = vatRate;
  };

  @action resetVatRate = () => {
    this.vatRate = undefined;
  };

  @action setCurrentPosition = (position: number): void => {
    this.currentPosition = position;
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  @computed
  get tablePositionsArray(): Position[] {
    return Object.values(this.tablePositions);
  }

  @action addPositionsFromObject = (positions: Position[]) => {
    const result: PositionsByKey = {};

    each(positions, (position: Position) => {
      const { key } = position;

      const vatNumber = Number(position.vat);

      result[key] = {
        ...position,
        part: camelizeKeys(position.part),
        // NOTICE: temp hack due to changes in TR VAT https://xometry.atlassian.net/browse/XTR-856
        vat: vatNumber != 20 && vatNumber != 0 ? 20 : position.vat,
      };
    });

    this.tablePositions = {
      ...this.tablePositions,
      ...result,
    };
  };

  @computed
  get positionsByShippingArray(): Position[] {
    return Object.keys(this.positionsByShipping).reduce((acc: Position[], cur) => {
      return [...acc, ...this.positionsByShipping[cur]];
    }, []);
  }

  @computed
  get nonDestroyedPositions(): Position[] {
    return this.positionsByShippingArray.filter((pos) => !pos._destroy);
  }

  @computed
  get positionsByShippingByKey() {
    return this.positionsByShippingArray.reduce((acc: Record<string, Position>, position) => {
      acc[position.key] = position;

      return acc;
    }, {});
  }

  @action addPositions = (positions: Position[]) => {
    const positionsByShipping = groupBy(positions, 'refSource');

    const newPositionsByShipping = Object.keys(positionsByShipping).reduce((acc: PositionsByShipping, source) => {
      if (['null', 'Deal::OrderConfirmation'].includes(source) || !source.startsWith('SH')) {
        const currentNullables = acc?.['null'] || [];
        const nullableObjs = positionsByShipping[source].map((pos) => ({ ...pos, refSource: 'null' }));

        return { ...acc, null: [...currentNullables, ...nullableObjs] };
      }

      return { ...acc, [source]: positionsByShipping[source] };
    }, {}) as PositionsByShipping;

    const firstSource = Object.keys(newPositionsByShipping)[0];

    if (this.positionsByShipping[firstSource]) {
      this.positionsByShipping[firstSource] = [
        ...this.positionsByShipping[firstSource],
        ...newPositionsByShipping[firstSource],
      ];
    } else {
      this.positionsByShipping = {
        ...this.positionsByShipping,
        ...newPositionsByShipping,
      };
    }

    const newPositions = positions.reduce((acc: Record<string, Position>, position) => {
      acc[position.key] = {
        ...position,
        part: camelizeKeys(position.part),
      };

      return acc;
    }, {});

    this.tablePositions = {
      ...this.tablePositions,
      ...newPositions,
    };
  };

  @action setPositions = (positions: Position[]) => {
    this.positionsByShipping = groupBy(positions, 'refSource');

    this.tablePositions = positions.reduce((acc: Record<string, Position>, position) => {
      acc[position.key] = {
        ...position,
        part: camelizeKeys(position.part),
      };

      return acc;
    }, {});
  };

  @action add = (): Position => {
    const currency = this.tablePositionsArray[0]?.priceMoney?.currencyCode;
    const key = uniqID();

    const newPosition = {
      categoryName: ServiceType.Packaging,
      name: packagingService.defaultName,
      quantity: 1,
      price: 0,
      priceMoney: {
        amount: '0',
        amountCents: '0',
        currencyCode: currency || getRegionCurrency(),
      },
      providerPrice: 0,
      vat: XOMETRY_VAT_ENABLED ? this.vatRate : this.rootStore.dealStore.data.vat,
      position: this.incCurrentPosition(),
      refSource: 'null',
      key,
    };

    this.tablePositions = {
      ...this.tablePositions,
      [key]: newPosition,
    };

    if (this.positionsByShipping['null'] && this.positionsByShipping['null'].length > 0) {
      this.positionsByShipping = {
        ...this.positionsByShipping,
        null: [...this.positionsByShipping['null'], newPosition],
      };
    } else {
      this.positionsByShipping = {
        ...this.positionsByShipping,
        null: [newPosition],
      };
    }

    return newPosition;
  };

  @action addCertificates = (certificates: Certificates): Position[] => {
    const newPositions = map(certificates, (data, certificate) => {
      const key = uniqID();

      const quantity = data?.positions || 1;
      const price = Number(((data?.amount || 0) / quantity).toFixed(2));

      const newPosition = {
        categoryName: ServiceType.Certificates,
        name: find(certificatesCollection.children, { value: certificate })?.defaultName || '',
        quantity,
        price,
        priceMoney: XMoney(data?.amountMoney ?? 0, data?.amountMoney.currencyCode).toIMoney(),
        providerPrice: 0,
        vat: XOMETRY_VAT_ENABLED ? this.vatRate : this.rootStore.dealStore.data.vat,
        position: this.incCurrentPosition(),
        key,
      };

      this.tablePositions = {
        ...this.tablePositions,
        [key]: newPosition,
      };

      if (this.positionsByShipping['null'] && this.positionsByShipping['null'].length > 0) {
        this.positionsByShipping = {
          ...this.positionsByShipping,
          null: [...this.positionsByShipping['null'], newPosition],
        };
      } else {
        this.positionsByShipping = {
          ...this.positionsByShipping,
          null: [newPosition],
        };
      }

      return newPosition;
    });

    return newPositions;
  };

  @action delete = (positionId: string, source?: string | null): void => {
    if (source || source === null) {
      this.positionsByShipping[String(source)] =
        this.positionsByShipping[String(source)]?.map((position) => {
          if (position.key === positionId) {
            return {
              ...position,
              _destroy: true,
            };
          }

          return position;
        }) ?? [];

      // Force a refresh of the positions comparison by updating the tablePositions
      // This will trigger the outcomingInvoicesAccountingFields query on subsequent position removals
      const position = this.tablePositions[positionId];

      if (position) {
        this.tablePositions[positionId] = {
          ...position,
          _destroy: true,
        };
      }

      this.positionsByShipping = { ...this.positionsByShipping };
    }
  };

  @action setTableLoading = (isLoading: boolean): void => {
    this.tableLoading = isLoading;
  };

  @action updatePositionAndCloseEdit = (key: string, position: Position): void => {
    const replaceObject = (replacement: Position) => {
      Object.keys(this.positionsByShipping).map((prop) => {
        this.positionsByShipping[prop] = this.positionsByShipping[prop].map((item) => {
          if (item.key === replacement.key) {
            return replacement;
          } else {
            return item;
          }
        });
      });
    };

    replaceObject(position);
    this.tablePositions[key] = position;

    this.tablePositions = { ...this.tablePositions };
    this.positionsByShipping = { ...this.positionsByShipping };
  };

  @action reset = (): void => {
    this.rootStore.dealOutInvoicePositionsStore = new DealOutInvoicePositionsStore(this.rootStore);
  };

  @action setSourceShipping = (shipping: OIShippingDetails) => {
    this.sourceShipping = shipping;
  };

  @action updateVat = () => {
    const updatedData: PositionsByShipping = {};

    Object.keys(this.positionsByShipping).forEach((key) => {
      updatedData[key] = this.positionsByShipping[key].map((item) => ({ ...item, vat: this.vatRate }));
    });

    this.positionsByShipping = updatedData;

    const updatedTablePositions: PositionsByKey = {};

    Object.keys(this.tablePositions).forEach((key) => {
      updatedTablePositions[key] = { ...this.tablePositions[key], vat: this.vatRate };
    });

    this.tablePositions = updatedTablePositions;

    this.syncVatToForm();
  };

  // The form is seeded with a snapshot of `tablePositions`, so it would still submit the VAT the rows were loaded
  // with instead of the rate calculated on BE.
  private syncVatToForm = (): void => {
    const { form } = this.rootStore.dealOutInvoiceEditStore;
    const { vatRate } = this;

    if (!form || vatRate == null) return;

    const positions = Object.keys(this.tablePositions).reduce((acc: Record<string, { vat: number }>, key) => {
      if (form.getFieldValue(['positions', key]) != null) {
        acc[key] = { vat: vatRate };
      }

      return acc;
    }, {});

    form.setFieldsValue({ positions });
  };
}

export default DealOutInvoicePositionsStore;
